import {useEffect, useState} from 'react'
import {Card, Form, Button, Row, Col, Spinner, Modal} from 'react-bootstrap'
import {useQuery, useMutation, useLazyQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {BILLING_MARKUPS, ADMIN_BILLING_REPORT, AGENT_CALL_DETAIL} from '../../gql/queries/twilioBilling'
import {SET_BILLING_MARKUPS} from '../../gql/mutations/twilioBilling'
import {useTableSort} from '../../helpers/useTableSort'

const money = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

const formatE164 = (e164: string) => {
  const m = (e164 || '').match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : e164
}

// Admin: rebilling markups + per-agent cost / charge / margin (personal numbers).
export const RebillingTab = () => {
  const {data: mkData, refetch: refetchMarkups} = useQuery(BILLING_MARKUPS, {fetchPolicy: 'cache-and-network'})
  const {data: repData} = useQuery(ADMIN_BILLING_REPORT, {fetchPolicy: 'cache-and-network'})
  const [setMarkups, {loading: saving}] = useMutation(SET_BILLING_MARKUPS)
  const report = repData?.adminBillingReport
  const mk = mkData?.billingMarkups

  const {onSort, sortRows, arrow} = useTableSort<any>('charge_total', 'desc')
  const [pct, setPct] = useState('')
  const [dollars, setDollars] = useState('')
  const base = Number(mk?.rental_ref_cost) || 1.15
  useEffect(() => {
    if (mk) {
      setPct(String(mk.markup_pct))
      setDollars(String(mk.markup_dollars))
    }
  }, [mk])

  // % and $ stay in sync via the reference rental ($1.15). Editing one updates the other.
  const onPct = (v: string) => {
    const clean = v.replace(/[^0-9.]/g, '')
    setPct(clean)
    const num = parseFloat(clean)
    setDollars(isNaN(num) ? '' : ((num / 100) * base).toFixed(2))
  }
  const onDollars = (v: string) => {
    const clean = v.replace(/[^0-9.]/g, '')
    setDollars(clean)
    const num = parseFloat(clean)
    setPct(isNaN(num) ? '' : ((num / base) * 100).toFixed(2))
  }

  const save = async () => {
    try {
      await setMarkups({variables: {voiceMarkupPct: parseFloat(pct)}})
      toast.success('Markup saved.')
      await refetchMarkups()
    } catch (err: any) {
      toast.error(err?.message || 'Could not save markup')
    }
  }

  // Call-detail drill-down
  const [runDetail, {data: detailData, loading: detailLoading}] = useLazyQuery(AGENT_CALL_DETAIL, {
    fetchPolicy: 'network-only',
  })
  const [detailAgent, setDetailAgent] = useState('')
  const [showDetail, setShowDetail] = useState(false)
  const detail = detailData?.agentCallDetail
  const openDetail = (a: any) => {
    setDetailAgent(a.agent || a.email)
    setShowDetail(true)
    runDetail({variables: {targetUserId: a.owner_id}})
  }

  return (
    <div className='mt-5'>
      <div className='mb-5'>
        <h2 className='mb-1'>Agent Rebilling</h2>
        <span className='text-muted'>Charge agents for their personal numbers (rental + voice) with a margin.</span>
      </div>

      <Card className='mb-4'>
        <Card.Header>
          <Card.Title>Markups</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className='g-3 align-items-end'>
            <Col xs='auto'>
              <Form.Label>Markup (%)</Form.Label>
              <Form.Control style={{maxWidth: 120}} value={pct} onChange={(e: any) => onPct(e.target.value)} />
            </Col>
            <Col xs='auto'>
              <Form.Label>≈ $ (per ${base.toFixed(2)} rental)</Form.Label>
              <Form.Control style={{maxWidth: 120}} value={dollars} onChange={(e: any) => onDollars(e.target.value)} />
            </Col>
            <Col xs='auto'>
              <Button variant='primary' onClick={save} disabled={saving}>
                {saving ? <Spinner size='sm' animation='border' /> : 'Save markup'}
              </Button>
            </Col>
            <Col>
              <span className='text-muted fs-8'>
                One markup — % and $ stay in sync — applied to <b>both</b> rental and voice:
                charge = cost × (1 + markup%). Global default.
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title>By agent — this month</Card.Title>
        </Card.Header>
        <Card.Body className='p-0'>
          <div className='table-responsive'>
            <table className='table table-row-dashed align-middle gy-2 mb-0'>
              <thead>
                <tr className='fw-bold text-muted' style={{cursor: 'pointer'}}>
                  <th className='ps-6' onClick={() => onSort('agent')}>Agent{arrow('agent')}</th>
                  <th>Numbers</th>
                  <th onClick={() => onSort('minutes')}>Time / Price{arrow('minutes')}</th>
                  <th onClick={() => onSort('rental_charge_total')}>Cost rent{arrow('rental_charge_total')}</th>
                  <th onClick={() => onSort('charge_total')}>Agent charge{arrow('charge_total')}</th>
                  <th className='text-end' onClick={() => onSort('margin_total')}>Margin{arrow('margin_total')}</th>
                  <th className='text-end pe-6'></th>
                </tr>
              </thead>
              <tbody>
                {sortRows(report?.agents ?? []).map((a: any) => (
                  <tr key={a.email || a.agent}>
                    <td className='ps-6 fw-bold'>{a.agent || a.email}</td>
                    <td>
                      {(a.numbers_list ?? []).map((num: string) => (
                        <div key={num} className='fs-7'>{formatE164(num)}</div>
                      ))}
                    </td>
                    <td className='text-muted'>
                      <div>{a.minutes} min · {money(a.voice_charge_total)}</div>
                      <div className='fs-8'>{a.calls} calls</div>
                    </td>
                    <td>{money(a.rental_charge_total)}</td>
                    <td>{money(a.charge_total)}</td>
                    <td className='text-end fw-semibold text-success'>{money(a.margin_total)}</td>
                    <td className='text-end pe-6'>
                      <Button variant='light-primary' size='sm' onClick={() => openDetail(a)}>
                        View calls
                      </Button>
                    </td>
                  </tr>
                ))}
                {report && (report.agents ?? []).length > 0 && (
                  <tr className='fw-bold'>
                    <td className='ps-6'>Total</td>
                    <td />
                    <td className='text-muted'>
                      <div>{report.minutes_total} min · {money(report.voice_charge_total)}</div>
                      <div className='fs-8'>{report.calls_total} calls</div>
                    </td>
                    <td>{money(report.rental_charge_total)}</td>
                    <td>{money(report.charge_total)}</td>
                    <td className='text-end text-success'>{money(report.margin_total)}</td>
                    <td className='pe-6' />
                  </tr>
                )}
              </tbody>
            </table>
            {report && (report.agents ?? []).length === 0 && (
              <p className='text-muted mb-0 p-6'>No agent numbers billed this month yet.</p>
            )}
          </div>
        </Card.Body>
      </Card>

      <Modal show={showDetail} onHide={() => setShowDetail(false)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Call consumption — {detailAgent}
            {detail ? <span className='text-muted fs-6 fw-normal'> · {detail.count} calls · ${Number(detail.charge_total).toFixed(2)}</span> : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className='text-center py-6'><Spinner animation='border' size='sm' /></div>
          ) : !detail || (detail.calls ?? []).length === 0 ? (
            <p className='text-muted mb-0'>No calls this month.</p>
          ) : (
            <div className='table-responsive' style={{maxHeight: 420, overflowY: 'auto'}}>
              <table className='table table-row-dashed align-middle gy-2 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>When</th>
                    <th>Dir</th>
                    <th>My number</th>
                    <th>Other party</th>
                    <th>Time</th>
                    <th className='text-end'>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.calls.map((c: any, i: number) => (
                    <tr key={i}>
                      <td className='text-muted'>{(c.started_at || '').slice(0, 16)}</td>
                      <td>
                        <span className={`badge ${c.direction === 'in' ? 'badge-light-success' : 'badge-light-primary'}`}>
                          {c.direction === 'in' ? 'Inbound' : 'Outbound'}
                        </span>
                      </td>
                      <td>{formatE164(c.my_number)}</td>
                      <td>{formatE164(c.other_party)}</td>
                      <td className='text-muted'>{c.duration_sec}s</td>
                      <td className='text-end fw-semibold'>${Number(c.charge).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}
